/**
 * useAuth hook - Updated for username/password authentication
 */

import { useEffect, useState } from 'react';
import { useAuthStore } from '../stores/auth';
import { getAPIClient } from '../api/client';
import { useToastStore } from '../stores/toast';
import { login as loginAPI, register as registerAPI, logout as logoutAPI, refreshToken as refreshTokenAPI } from '../api/auth';
import type { LoginRequest, RegisterRequest } from '../api/auth';

export function useAuth() {
  const { 
    apiKey, 
    accessToken, 
    refreshToken,
    user,
    username, 
    isAuthenticated, 
    setApiKey, 
    setAccessToken,
    setUser,
    setUsername, 
    logout 
  } = useAuthStore();
  const { addToast } = useToastStore();
  const [isValidating, setIsValidating] = useState(false);

  // Initialize API client with token
  useEffect(() => {
    const client = getAPIClient();
    
    // Prefer JWT token over API key
    if (accessToken) {
      client.setAccessToken(accessToken);
      console.log('[Auth] JWT token set on client');
    } else if (apiKey) {
      client.setApiKey(apiKey);
      console.log('[Auth] API key set on client:', apiKey.substring(0, 10) + '...');
    } else {
      // Only clear auth if we explicitly don't have a token
      // Don't clear if token was set in main.tsx before zustand loaded
      // The token will be cleared on logout anyway
      if (client.hasAuth()) {
        console.log('[Auth] Store has no token, but API client has one - keeping it (may be from main.tsx)');
      } else {
        client.clearAuth();
      }
    }
  }, [apiKey, accessToken]);

  // Auto-refresh token before expiration
  useEffect(() => {
    if (!refreshToken || !accessToken) return;

    const tokenExpiry = JSON.parse(atob(accessToken.split('.')[1])).exp * 1000;
    const timeUntilExpiry = tokenExpiry - Date.now();
    const refreshTime = timeUntilExpiry - 60000; // Refresh 1 minute before expiry

    if (refreshTime > 0) {
      const timer = setTimeout(async () => {
        try {
          const response = await refreshTokenAPI({ refresh_token: refreshToken });
          setAccessToken(response.access_token, refreshToken, user || undefined);
          addToast({
            message: 'Session refreshed',
            type: 'success',
          });
        } catch (error) {
          console.error('[Auth] Token refresh failed:', error);
          logout();
          addToast({
            message: 'Session expired. Please log in again.',
            type: 'error',
          });
        }
      }, refreshTime);

      return () => clearTimeout(timer);
    }
  }, [accessToken, refreshToken, user, setAccessToken, logout, addToast]);

  const login = async (request: LoginRequest) => {
    console.log('[Auth] Login called for:', request.username);
    
    setIsValidating(true);
    try {
      const response = await loginAPI(request);
      
      // Store tokens and user info
      setAccessToken(response.access_token, response.refresh_token, response.user);
      
      // Set API client token
      const client = getAPIClient();
      client.setAccessToken(response.access_token);
      
      addToast({
        message: 'Successfully signed in!',
        type: 'success',
      });
    } catch (error: any) {
      console.error('[Auth] Login failed:', error);
      const message = error.response?.data?.detail || error.message || 'Login failed. Please check your credentials.';
      addToast({
        message,
        type: 'error',
      });
      throw error;
    } finally {
      setIsValidating(false);
    }
  };

  const register = async (request: RegisterRequest) => {
    console.log('[Auth] Register called for:', request.email);
    
    setIsValidating(true);
    try {
      const response = await registerAPI(request);
      
      addToast({
        message: response.message || 'Registration successful! Please verify your email.',
        type: 'success',
      });
      
      return response;
    } catch (error: any) {
      console.error('[Auth] Registration failed:', error);
      const message = error.response?.data?.detail || error.message || 'Registration failed. Please try again.';
      addToast({
        message,
        type: 'error',
      });
      throw error;
    } finally {
      setIsValidating(false);
    }
  };

  const handleLogout = async () => {
    try {
      if (accessToken) {
        await logoutAPI();
      }
    } catch (error) {
      console.error('[Auth] Logout API call failed:', error);
    } finally {
      // Clear local state regardless of API call result
      logout();
      const client = getAPIClient();
      client.clearAuth();
      
      // Clear mock role on logout
      try {
        const { clearMockRoleOnLogout } = await import('./useRoleSwitch');
        clearMockRoleOnLogout();
      } catch (error) {
        console.warn('[Auth] Failed to clear mock role on logout:', error);
      }
      
      addToast({
        message: 'Logged out successfully',
        type: 'success',
      });
      
      // Redirect to login page
      // Use window.location to ensure a full page reload and clear any state
      // Add logout=true query param to prevent auto-redirect back to home
      const baseUrl = window.location.origin;
      const loginPath = '/login?logout=true';
      window.location.href = baseUrl + loginPath;
    }
  };

  return {
    apiKey,
    accessToken,
    user,
    username,
    isAuthenticated,
    login,
    register,
    logout: handleLogout,
    isValidating,
  };
}
